import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useStore } from '../lib/store.js';
import StatusPill from '../components/StatusPill.js';
import ActionPanel from '../components/ActionPanel.js';
import Console from '../components/Console.js';
import MapQueue from '../components/MapQueue.js';
import BackupsPanel from '../components/BackupsPanel.js';
import SnapshotsPanel from '../components/SnapshotsPanel.js';
import SettingsPanel from '../components/SettingsPanel.js';
import { uptime, fmtDate, fmtLongDate, relTime, kbps, mbytes } from '../lib/format.js';
import { api } from '../lib/api.js';
import {
  IconActivity,
  IconCalendar,
  IconConsole,
  IconMap,
  IconSettings,
  IconWarning,
} from '../components/Icons.js';
import { MetricChart, RangePicker, type Range } from '../components/MetricChart.js';
import { LoadingCenter } from '../components/Spinner.js';

type Tab = 'console' | 'maps' | 'stats' | 'settings';

export default function ServerDetail() {
  const { id } = useParams();
  const sid = parseInt(id ?? '0', 10);

  const server = useStore((s) => s.servers.get(sid));
  const live = useStore((s) => s.liveStats.get(sid));
  const nodeOnline = useStore((s) => server ? !!s.nodeStatus.get(server.Server_Machine)?.online : false);
  const me = useStore((s) => s.user);
  const hasSnapshot = useStore((s) => s.hasSnapshot);
  const [tab, setTab] = useState<Tab>('console');
  const [statsRange, setStatsRange] = useState<Range>('6h');
  const [nextWipe, setNextWipe] = useState<number | null>(null);

  useEffect(() => {
    if (!server || !server.Auto_Wipe || !server.Wipe_Cron) {
      setNextWipe(null);
      return;
    }
    let cancelled = false;
    const fetchNext = () => {
      api.get<{ next_wipe: number | null }>(`/api/servers/${server.Server_ID}`)
        .then((r) => { if (!cancelled) setNextWipe(r.next_wipe); })
        .catch(() => {});
    };
    fetchNext();
    const t = setInterval(fetchNext, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [server?.Server_ID, server?.Auto_Wipe, server?.Wipe_Cron]);

  if (!server) {
    if (!hasSnapshot) {
      return <LoadingCenter label={`Loading server #${sid}…`} />;
    }
    return (
      <div className="card p-10 text-center max-w-md mx-auto">
        <h2 className="text-lg font-semibold mb-2">Server not found</h2>
        <Link to="/" className="text-accent hover:underline">← Back to dashboard</Link>
      </div>
    );
  }

  const status = !nodeOnline ? 'offline' : live?.status ?? 'offline';
  // When the agent is unreachable, cached rcon data is stale by definition —
  // hide it instead of presenting it as the current state of the server.
  const rcon = nodeOnline ? live?.rcon : undefined;

  const tabs: Array<{ key: Tab; label: string; icon: React.ReactNode }> = [
    { key: 'console',  label: 'Console',  icon: <IconConsole className="w-3.5 h-3.5" /> },
    { key: 'maps',     label: 'Maps',     icon: <IconMap className="w-3.5 h-3.5" /> },
    { key: 'stats',    label: 'Stats',    icon: <IconActivity className="w-3.5 h-3.5" /> },
    { key: 'settings', label: 'Settings', icon: <IconSettings className="w-3.5 h-3.5" /> },
  ];

  return (
    <div className="space-y-6">
      {/* Header card */}
      <div className="card p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-semibold tracking-tight">{server.Server_Name}</h1>
              <StatusPill status={status} />
              <span className="text-xs font-mono text-fg-dim">#{server.Server_ID}</span>
            </div>
            <div className="text-sm text-fg-muted mt-1.5">
              <Link to={`/machines/${encodeURIComponent(server.Server_Machine)}`} className="hover:text-accent transition-colors">
                {server.Server_Machine}
              </Link>
              <span className="text-fg-dim mx-1.5">·</span>
              {server.Server_Group}
              {server.Server_Org && (
                <>
                  <span className="text-fg-dim mx-1.5">·</span>
                  {server.Server_Org}
                </>
              )}
            </div>
            {rcon?.Hostname && (
              <div className="text-xs text-fg-dim mt-1 font-mono truncate max-w-2xl" title={rcon.Hostname}>
                {rcon.Hostname}
              </div>
            )}
          </div>
          <div className="text-right text-xs text-fg-dim space-y-1 shrink-0">
            <div>IP <span className="text-fg-muted font-mono ml-1">{server.Server_IP}</span></div>
            <div>RCON <span className="text-fg-muted font-mono ml-1">:{server.Server_rcon_port}</span></div>
            <div title="Last successful Steam + Oxide update">
              Last update{' '}
              {server.Last_Game_Update ? (
                <span className="text-fg-muted ml-1">{fmtDate(server.Last_Game_Update)}</span>
              ) : (
                <span className="italic ml-1">never</span>
              )}
            </div>
          </div>
        </div>

        {/* Offline banner */}
        {!nodeOnline && (
          <div className="mt-5 rounded-md border border-status-error/40 bg-status-error/5 px-3 py-2 text-sm text-fg-muted">
            <strong className="text-status-error">Machine <Link to={`/machines/${encodeURIComponent(server.Server_Machine)}`} className="underline hover:text-status-error">{server.Server_Machine}</Link> is unreachable.</strong>{' '}
            Server actions are disabled and live stats are paused until the agent reconnects.
          </div>
        )}

        {/* Live stats grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5 mt-5">
          <Tile label="Players" value={rcon ? `${rcon.Players ?? 0}/${rcon.MaxPlayers ?? '—'}` : '—'} sub={rcon?.Joining ? `${rcon.Joining} joining` : ''} />
          <Tile label="FPS" value={rcon?.Framerate != null ? `${rcon.Framerate}` : '—'} sub="server tick" />
          <Tile label="Entities" value={rcon?.EntityCount != null ? `${rcon.EntityCount}` : '—'} sub="" />
          <Tile label="Memory" value={rcon?.MemoryUsageSystem != null ? mbytes(rcon.MemoryUsageSystem) : '—'} sub="" />
          <Tile label="Net In" value={rcon?.NetworkIn != null ? kbps(rcon.NetworkIn) : '—'} sub="" />
          <Tile label="Uptime" value={rcon?.Uptime != null ? uptime(rcon.Uptime) : '—'} sub="since start" />
        </div>

        {server.Auto_Wipe && server.Wipe_Cron && (
          <div className="mt-4 pt-4 border-t border-bg-border flex items-center gap-2 text-sm flex-wrap">
            <span className="text-accent"><IconCalendar /></span>
            <span className="text-fg-dim">Next wipe</span>
            {nextWipe ? (
              <>
                <span className="text-fg font-medium">{fmtLongDate(nextWipe)}</span>
                <span className="text-fg-dim">·</span>
                <span className="text-fg-muted">{relTime(nextWipe)}</span>
              </>
            ) : (
              <span className="text-fg-dim italic">computing…</span>
            )}
            <span className="text-fg-dim ml-auto text-xs">First-Thursday auto-skipped</span>
          </div>
        )}
      </div>

      {/* Body grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-3">
          {/* Tabs as a pill bar */}
          <div className="tab-bar">
            {tabs.map((t) => {
              const mapsCount = server.maps.length;
              const wipePending = server.Auto_Wipe && !!server.Wipe_Cron;
              const showWarning = t.key === 'maps' && wipePending && mapsCount === 0;
              const showCount = t.key === 'maps' && mapsCount > 0;
              const isActive = tab === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  className={`tab-item ${isActive ? 'tab-item-active' : ''}`}
                  aria-pressed={isActive}
                >
                  <span className={isActive ? 'text-accent' : 'text-fg-dim'}>{t.icon}</span>
                  <span>{t.label}</span>
                  {showCount && (
                    <span
                      className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 text-[10px] font-semibold tabular-nums rounded-full bg-accent/15 text-accent"
                      title={`${mapsCount} map${mapsCount === 1 ? '' : 's'} queued`}
                    >
                      {mapsCount}
                    </span>
                  )}
                  {showWarning && (
                    <span title="Auto-wipe is scheduled but no maps are queued" className="text-status-warn">
                      <IconWarning className="w-3.5 h-3.5" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          {tab === 'console' && <Console server={server} nodeOnline={nodeOnline} />}
          {tab === 'maps' && <MapQueue server={server} />}
          {tab === 'stats' && (
            <ServerStats Server_ID={sid} range={statsRange} onRangeChange={setStatsRange} />
          )}
          {tab === 'settings' && (
            <SettingsPanel
              server={server}
              canEdit={me?.role === 'admin' || me?.role === 'operator'}
            />
          )}
        </div>
        <div className="space-y-5">
          <ActionPanel
            key={server.Server_ID}
            server={server}
            live={live}
            nodeOnline={nodeOnline}
          />
          <BackupsPanel Server_ID={sid} canRestore={me?.role === 'admin'} />
          <SnapshotsPanel
            Server_ID={sid}
            Server_Name={server.Server_Name}
            serverOffline={status === 'offline'}
            canManage={me?.role === 'admin'}
          />
        </div>
      </div>
    </div>
  );
}

function Tile({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="surface px-3 py-2.5">
      <div className="text-[10px] uppercase tracking-wider text-fg-dim font-semibold">{label}</div>
      <div className="text-lg font-semibold tabular-nums mt-0.5 text-fg">{value}</div>
      {sub && <div className="text-[10px] text-fg-dim mt-0.5">{sub}</div>}
    </div>
  );
}

function ServerStats({ Server_ID, range, onRangeChange }: { Server_ID: number; range: Range; onRangeChange: (r: Range) => void }) {
  const endpoint = `/api/metrics/server/${Server_ID}`;
  return (
    <div className="space-y-4">
      <div className="card p-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm text-fg-muted">
          History · samples every 30s · retained 7 days
        </div>
        <RangePicker value={range} onChange={onRangeChange} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <MetricChart endpoint={endpoint} range={range} dataKey="players"   label="Players"    stroke="#34d399" />
        <MetricChart endpoint={endpoint} range={range} dataKey="fps"       label="Server FPS" stroke="#5fa8ff" />
        <MetricChart endpoint={endpoint} range={range} dataKey="entities"  label="Entities"   stroke="#f5b544" />
        <MetricChart endpoint={endpoint} range={range} dataKey="memory_mb" label="Memory"     stroke="#f56565" format={mbytes} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <MetricChart endpoint={endpoint} range={range} dataKey="network_in" label="Network In" stroke="#a78bfa" format={kbps} />
      </div>
    </div>
  );
}
